import React from 'react';
import {LatLng} from 'leaflet';
import {CircleMarker, Pane, Polyline, Tooltip} from 'react-leaflet';
import {useTranslation} from 'react-i18next';
import {
	HALO_RADIUS_ROUTE_CHANGE,
	RING_RADIUS_ROUTE_CHANGE,
	ROUTE_CHANGE_PATH
} from "@containers/singleVoyage/voyageTemplates/routeChange/routeChangeStyle";

export interface IMovedStopRouteChange {
	key: string | number
	/** stop number as the user sees it, from 1 */
	number: number
	position: LatLng
	/** where it stood before, when known */
	before?: LatLng | null
	distance?: number | null
}

interface IProps {
	stops: IMovedStopRouteChange[]
	/** the stop markers' pane z-index: the halos go just under them */
	markersZIndex: number
	/** lifts the label clear of the stop marker */
	tooltipOffset: number
	/** labels always shown; off where the map is zoomed out and they would cover other stops */
	permanentLabels?: boolean
}

/**
 * A moved stop and the spot it left: the same halo on both, orange where it is and light blue
 * where it was, joined by a dashed line, so the pair reads as one even when far apart.
 */
const MovedStopsRouteChange = ({stops, markersZIndex, tooltipOffset, permanentLabels = true}: IProps) => {

	const {t} = useTranslation()

	return (
		<>
			<Pane name={'routeChangeOldStops'} style={{zIndex: markersZIndex - 10}}>
				{stops.filter((stop) => !!stop.before).map((stop) => (
					<React.Fragment key={stop.key}>
						<Polyline positions={[stop.before, stop.position]} pathOptions={ROUTE_CHANGE_PATH.connector}/>
						<CircleMarker center={stop.before} radius={HALO_RADIUS_ROUTE_CHANGE} pathOptions={ROUTE_CHANGE_PATH.oldHalo}/>
						<CircleMarker center={stop.before} radius={RING_RADIUS_ROUTE_CHANGE} pathOptions={ROUTE_CHANGE_PATH.oldRing}>
							<Tooltip direction={'top'} offset={[0, -RING_RADIUS_ROUTE_CHANGE - 4]} permanent={permanentLabels}>
								{t('singleVoyage.voyageTemplate.sync.oldStopTooltip', {number: stop.number})}
							</Tooltip>
						</CircleMarker>
					</React.Fragment>
				))}
			</Pane>

			<Pane name={'routeChangeMovedStops'} style={{zIndex: markersZIndex - 5}}>
				{stops.map((stop) => (
					<CircleMarker
						key={stop.key}
						center={stop.position}
						radius={HALO_RADIUS_ROUTE_CHANGE}
						pathOptions={ROUTE_CHANGE_PATH.movedHalo}
					>
						<Tooltip direction={'top'} offset={[0, -tooltipOffset]} permanent={permanentLabels}>
							{stop.distance
								? t('singleVoyage.voyageTemplate.sync.movedStopTooltipDistance', {number: stop.number, distance: stop.distance})
								: t('singleVoyage.voyageTemplate.sync.movedStopTooltip', {number: stop.number})}
						</Tooltip>
					</CircleMarker>
				))}
			</Pane>
		</>
	);
};

export default MovedStopsRouteChange;
